/**
 * Browser-side loader for the cross-locale alias maps the live biomarker /
 * disease explorer pages use.
 *
 * Mirrors `lib/search/client.ts`: pulls a static JSON from `public/` once,
 * memoizes it, `force-cache`. We deliberately do NOT bundle these maps into
 * the route JS — they're only needed when the user actually searches, so
 * loading them eagerly would tax hydration (TBT/INP) on every catalog-page
 * visit. Fetched on search intent, parsed off the critical path.
 *
 * The files are built by `scripts/generate-cross-locale-aliases.ts` and are
 * gitignored (regenerated on every deploy), so in local dev without a build
 * the fetch 404s and `loadCrossLocaleAliases` returns `{}` — search still
 * works on the current locale's own names, just without cross-language reach.
 */

export type CrossLocaleAliasMap = Record<string, string[]>

type Collection = "biomarker" | "disease"

const cache = new Map<Collection, Promise<CrossLocaleAliasMap>>()

async function fetchMap(collection: Collection): Promise<CrossLocaleAliasMap> {
  try {
    const res = await fetch(
      `/search-index/${collection}-cross-locale-aliases.json`,
      { cache: "force-cache" }
    )
    if (!res.ok) return {}
    return (await res.json()) as CrossLocaleAliasMap
  } catch {
    return {}
  }
}

export function loadCrossLocaleAliases(
  collection: Collection
): Promise<CrossLocaleAliasMap> {
  let entry = cache.get(collection)
  if (!entry) {
    entry = fetchMap(collection)
    cache.set(collection, entry)
  }
  return entry
}
